import { Action } from "redux";
import { GtmCheckoutStep, IGtmProductFromList, IGtmProductWithQuantity, IGtmPurchaseActionField } from "@montana/services/tracking/tracking";
import { IProductListVariant } from "@montana/models/generated/cms.interface";

export enum TrackingAction {
    PAGE_VIEW = "@@tracking/PAGE_VIEW",
    CHECKOUT_STEP = "@@tracking/CHECKOUT_STEP",
    TRANSACTION = "@@tracking/TRANSACTION",
    CUSTOM_EVENT = "@@tracking/CUSTOM_EVENT",
    PRODUCT_VIEW = "@@tracking/PRODUCT_VIEW",
    PRODUCT_IMPRESSION = "@@tracking/PRODUCT_IMPRESSION",
    PRODUCT_CLICK = "@@tracking/PRODUCT_CLICK",
    ADD_TO_CART = "@@tracking/ADD_TO_CART",
    REMOVE_FROM_CART = "@@tracking/REMOVE_FROM_CART",
    CHECKOUT_OPTION = "@@tracking/CHECKOUT_OPTION",
    PURCHASE = "@@tracking/PURCHASE",
}

export type ITrackingAction = ITrackPageViewAction | ITrackCheckoutStepAction | ITrackTransactionAction | ITrackCustomEventAction;

export interface ITrackPageViewAction extends Action<TrackingAction.PAGE_VIEW> {
    title: string;
}

export interface ITrackCheckoutStepAction extends Action<TrackingAction.CHECKOUT_STEP> {
    step: GtmCheckoutStep;
}

export type ITrackTransactionAction = Action<TrackingAction.TRANSACTION>;

export interface ITrackCustomEventAction extends Action<TrackingAction.CUSTOM_EVENT> {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
}

export type ITrackProductViewAction = Action<TrackingAction.PRODUCT_VIEW>;

export interface ITrackProductImpressionAction extends Action<TrackingAction.PRODUCT_IMPRESSION> {
    products: IProductListVariant[];
    category: string;
}

export interface ITrackProductClickAction extends Action<TrackingAction.PRODUCT_CLICK> {
    currency: string;
    product: IGtmProductFromList;
    category: string;
}

export interface ITrackAddToCartAction extends Action<TrackingAction.ADD_TO_CART> {
    currency: string;
    products: IGtmProductWithQuantity[];
}

export interface ITrackRemoveFromCartAction extends Action<TrackingAction.REMOVE_FROM_CART> {
    currency: string;
    products: IGtmProductWithQuantity[];
}

export interface ITrackCheckoutOptionAction extends Action<TrackingAction.CHECKOUT_OPTION> {
    step: GtmCheckoutStep;
    option: string;
}

export interface ITrackPurchaseAction extends Action<TrackingAction.PURCHASE> {
    currency: string;
    products: IGtmProductWithQuantity[];
    actionField: IGtmPurchaseActionField;
}

export const trackPageView = (title: string): ITrackPageViewAction => ({ type: TrackingAction.PAGE_VIEW, title });

export const trackCheckoutStep = (step: GtmCheckoutStep): ITrackCheckoutStepAction => ({ type: TrackingAction.CHECKOUT_STEP, step });

export const trackTransaction = (): ITrackTransactionAction => ({ type: TrackingAction.TRANSACTION });

export const trackCustomEvent = (eventCategory: string, eventAction: string, eventLabel: string): ITrackCustomEventAction =>
    ({ type: TrackingAction.CUSTOM_EVENT, eventCategory, eventAction, eventLabel });

export const trackProductView = (): ITrackProductViewAction => ({ type: TrackingAction.PRODUCT_VIEW });

export const trackProductImpression = (products: IProductListVariant[], category: string): ITrackProductImpressionAction => ({ type: TrackingAction.PRODUCT_IMPRESSION, products, category });

export const trackProductClick = (currency: string, product: IGtmProductFromList, category: string): ITrackProductClickAction => ({ type: TrackingAction.PRODUCT_CLICK, currency, product, category });

export const trackAddToCart = (currency: string, products: IGtmProductWithQuantity[]): ITrackAddToCartAction => ({ type: TrackingAction.ADD_TO_CART, currency, products });

export const trackRemoveFromCart = (currency: string, products: IGtmProductWithQuantity[]): ITrackRemoveFromCartAction => ({ type: TrackingAction.REMOVE_FROM_CART, currency, products });

export const trackCheckoutOption = (step: GtmCheckoutStep, option: string): ITrackCheckoutOptionAction => ({ type: TrackingAction.CHECKOUT_OPTION, step, option });

export const trackPurchase = (currency: string, products: IGtmProductWithQuantity[], actionField: IGtmPurchaseActionField): ITrackPurchaseAction => ({ type: TrackingAction.PURCHASE, currency, products, actionField });
